import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Check, WarningCircle } from 'iconoir-react';
import '../styles/register.css';

export const VerifyEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const verificationStartedRef = useRef(false);

  const email = location.state?.email || searchParams.get('email') || '';
  const token = searchParams.get('token');

  useEffect(() => {
    if (token && email && !verificationStartedRef.current) {
      verificationStartedRef.current = true;
      verifyEmail();
    }
  }, [token, email]);

  const verifyEmail = async () => {
    if (!token || !email) return;

    setStatus('verifying');
    try {
      const response = await authApi.verifyEmail({ email, token });
      setStatus('success');
      setMessage(response.data.message || 'Email vérifié avec succès !');

      if (isAuthenticated) {
        await refreshUser();
      }

      const pendingPlanId = response.data.pending_plan_id;
      const pendingBillingPeriod = response.data.pending_billing_period;

      setTimeout(() => {
        const storedReturnTo = localStorage.getItem('post_auth_redirect');

        const returnTo =
          storedReturnTo?.startsWith('/join/')
            ? storedReturnTo
            : null;

        if (returnTo) {
          localStorage.removeItem('post_auth_redirect');
          navigate(returnTo);
          return;
        }

        if (isAuthenticated && pendingPlanId) {
          navigate('/upgrade-plan', {
            state: { planId: pendingPlanId, billingPeriod: pendingBillingPeriod || 'monthly' },
          });
          return;
        }

        navigate(isAuthenticated ? '/dashboard' : '/login');
      }, 2000);
    } catch (err: any) {
      setStatus('error');
      setMessage(err.response?.data?.message || 'Le lien de vérification est invalide ou a expiré.');
    }
  };

  const handleResendEmail = async () => {
    if (!email) return;

    setResending(true);
    try {
      await authApi.resendVerification({ email });
      setMessage('Un nouvel email de vérification a été envoyé !');
    } catch (err: any) {
      setMessage(err.response?.data?.message || 'Erreur lors de l\'envoi de l\'email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <img src="/assets/ventilio.png" alt="Ventilio" className="verify-logo" />
        
        {status === 'pending' && (
          <div className="verify-content">
            <h1>Vérifiez votre email</h1>
            <p>
              Un email de vérification a été envoyé à <strong>{email}</strong>
            </p>
            <p>
              Veuillez cliquer sur le lien dans l'email pour activer votre compte.
            </p>
            <div className="verify-actions">
              <button 
                className="btn btn-secondary" 
                onClick={handleResendEmail}
                disabled={resending}
              >
                {resending ? 'Envoi en cours...' : 'Renvoyer l\'email'}
              </button>
            </div>
          </div>
        )}

        {status === 'verifying' && (
          <div className="verify-content">
            <div className="spinner"></div>
            <h1>Vérification en cours...</h1>
            <p>Veuillez patienter pendant que nous vérifions votre email.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="verify-content success">
            <div className="icon-circle success">
              <Check width={48} height={48} />
            </div>
            <h1>Email vérifié !</h1>
            <p>{message}</p>
            <p>Vous allez être redirigé vers vos tableaux...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="verify-content error">
            <div className="icon-circle error">
              <WarningCircle width={48} height={48} />
            </div>
            <h1>Erreur de vérification</h1>
            <p>{message}</p>
            <div className="verify-actions">
              <button 
                className="btn btn-secondary" 
                onClick={handleResendEmail}
                disabled={resending}
              >
                {resending ? 'Envoi en cours...' : 'Renvoyer l\'email'}
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => navigate('/login')}
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
